import { Star } from 'lucide-react';
import { PageSectionLarge } from '@/components/base/Sections';
import {
  TypographyH3,
  TypographyLabel,
  TypographySmall,
  TypographyText,
  TypographyTitle,
} from '@/components/base/Typography';
import '@/components/magicui/marquee';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import { AVATAR_COLORS, REVIEW_SOURCES } from '@/constants';
import { SecondaryCard } from '../base/Cards';

export const Specification = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <TypographyText className="block font-bold">{label}</TypographyText>
    {typeof value === 'string' || typeof value === 'number' ? (
      <TypographyText>{value}</TypographyText>
    ) : (
      value
    )}
  </div>
);

export const SimpleGuaranteeSection = () => {
  return (
    <Accordion type="single" collapsible className="space-y-4">
      <AccordionItem value="money-back">
        <AccordionTrigger className="hover:no-underline">
          <TypographyLabel className="text-lg tracking-wide">
            100% money back guarantee
          </TypographyLabel>
        </AccordionTrigger>
        <AccordionContent>
          <TypographyText>
            We stand behind every diamond we sell with a full money back guarantee.
          </TypographyText>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="returns-resizing">
        <AccordionTrigger className="hover:no-underline">
          <TypographyLabel className="text-lg tracking-wide">
            Free 30-day returns and resizing
          </TypographyLabel>
        </AccordionTrigger>
        <AccordionContent>
          <TypographyText>
            Free returns within 30 days and free resizing within 90 days of purchase.
          </TypographyText>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export const FAQSection = () => {
  return (
    <Accordion type="single" collapsible>
      {faqs.map((faq, index) => (
        <AccordionItem
          key={faq.id}
          value={faq.id}
          className={index === faqs.length - 1 ? 'border-none' : 'border-b border-gray-200'}
        >
          <AccordionTrigger className="hover:no-underline">
            <TypographyLabel className="text-lg tracking-wide">{faq.question}</TypographyLabel>
          </AccordionTrigger>
          <AccordionContent>
            <TypographyText>{faq.answer}</TypographyText>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

const faqs: any = [
  {
    id: 'lab-grown',
    question: 'What are lab-grown diamonds?',
    answer:
      'Lab-grown diamonds are real diamonds that are created in a laboratory using advanced technology. They have the same physical, chemical, and optical properties as mined diamonds.',
  },
  {
    id: 'certification',
    question: 'How do I know this diamond is authentic?',
    answer:
      "Most diamonds come with a certificate from IGI or GIA, the world's most trusted gemological institutes. The certificate includes detailed specifications and authenticity verification.",
  },
  {
    id: 'return-policy',
    question: 'What is your return policy?',
    answer:
      "We offer free 30-day returns with no questions asked. If you're not completely satisfied, return the diamond in its original condition for a full refund.",
  },
  {
    id: 'resizing',
    question: 'Do you offer ring resizing?',
    answer:
      'Yes, we provide free ring resizing within 90 days of purchase. After 90 days, resizing services are available for a small fee.',
  },
  {
    id: 'shipping',
    question: 'How long does shipping take?',
    answer:
      'We offer free insured shipping that typically takes 2-4 business days. Express shipping options are available for faster delivery.',
  },
  {
    id: 'warranty',
    question: 'What warranty do you provide?',
    answer:
      'All diamonds come with a lifetime warranty covering manufacturing defects. We also provide lifetime maintenance and cleaning services.',
  },
];

const reviews: any = [
  {
    id: 1,
    name: 'Sarah M.',
    rating: 5,
    date: '2 weeks ago',
    title: 'Beautiful setting, exactly as described',
    content:
      'I was nervous about buying a setting online, but this exceeded my expectations.\nThe quality is outstanding and the craftsmanship is perfect.',
    verified: true,
  },
  {
    id: 2,
    name: 'Michael R.',
    rating: 5,
    date: '1 month ago',
    title: 'Great value and fast shipping',
    content:
      'Compared prices everywhere and this was the best deal. Shipping was fast and secure. The setting is gorgeous and my fiancée loves it.',
    verified: true,
  },
  {
    id: 3,
    name: 'Jennifer L.',
    rating: 5,
    date: '3 weeks ago',
    title: 'Excellent customer service',
    content:
      'Had some questions before purchasing and the customer service team was incredibly helpful. The setting arrived exactly as promised.',
    verified: true,
  },
  {
    id: 4,
    name: 'David K.',
    rating: 5,
    date: '5 days ago',
    title: 'High quality craftsmanship',
    content:
      'The attention to detail in this setting is remarkable. Very pleased with the purchase and would definitely recommend.',
    verified: true,
  },
  {
    id: 5,
    name: 'Emily R.',
    rating: 5,
    date: '1 week ago',
    title: 'Perfect for my vintage diamond',
    content:
      'This setting was exactly what I was looking for. The vintage style complements my diamond perfectly. Shipping was quick too.',
    verified: true,
  },
  {
    id: 6,
    name: 'Robert T.',
    rating: 5,
    date: '2 months ago',
    title: 'Outstanding quality and service',
    content:
      'From ordering to delivery, everything was seamless. The setting quality exceeded my expectations and my partner absolutely loves it.',
    verified: true,
  },
];

export const Avatar = ({ name, bg }: { name: string; bg: string }) => (
  <div className={`h-10 w-10 ${bg} flex items-center justify-center rounded-full`}>
    <TypographySmall>
      {name
        .split(' ')
        .map((n) => n[0])
        .join('')}
    </TypographySmall>
  </div>
);

export const ReviewsSection = () => {
  const sources = REVIEW_SOURCES;
  const avatarColors = AVATAR_COLORS;

  return (
    <div className="space-y-8">
      <div>
        <TypographyH3 className="!font-sans">Customer Reviews</TypographyH3>
        <div className="mt-2 border-b border-gray-200"></div>
      </div>

      <div className="relative">
        <div className="mx-auto max-w-7xl">
          <Carousel opts={{ align: 'start', loop: false }}>
            <CarouselContent className="mx-0 my-1">
              {reviews.map((r, idx) => {
                const src = sources[idx % sources.length];

                return (
                  <CarouselItem key={r.id} className="mr-3 w-72 flex-shrink-0 basis-auto px-0">
                    <SecondaryCard className="p-4">
                      <div className="mb-4 flex gap-4">
                        <Avatar name={r.name} bg={avatarColors[idx % avatarColors.length]} />
                        <div>
                          <TypographyText className="font-semibold">{r.name} </TypographyText>
                          <TypographySmall className="text-muted-foreground">
                            {r.date}
                          </TypographySmall>
                        </div>
                      </div>

                      <div className="mb-3 flex gap-1">
                        {[...Array(5)].map((_, i) =>
                          src === 'Trustpilot' ? (
                            <div
                              key={i}
                              className={`h-4 w-4 ${i < r.rating ? 'bg-green-500' : 'bg-gray-300'}`}
                              style={{
                                clipPath:
                                  'polygon(50% 0%, 63% 38%, 100% 38%, 69% 59%, 82% 100%, 50% 75%, 18% 100%, 31% 59%, 0% 38%, 37% 38%)',
                              }}
                            />
                          ) : (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ),
                        )}
                      </div>

                      <div className="mb-3 flex-grow">
                        <TypographyText className="line-clamp-3 leading-relaxed">
                          "{r.content}"
                        </TypographyText>
                      </div>

                      <div className="mt-3 flex items-center justify-end">
                        <div className="flex items-center gap-1">
                          {src === 'Google' ? (
                            <>
                              <div className="h-4 w-4 rounded-sm bg-gradient-to-r from-blue-500 to-green-500" />
                              <TypographySmall className="font-medium text-gray-600">
                                Google
                              </TypographySmall>
                            </>
                          ) : (
                            <>
                              <div className="h-4 w-4 rounded-sm bg-gradient-to-r from-green-400 to-green-600" />
                              <TypographySmall className="font-medium text-gray-600">
                                Trustpilot
                              </TypographySmall>
                            </>
                          )}
                        </div>
                      </div>
                    </SecondaryCard>
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            {/* Buttons aligned to the container padding, non-blocking and visible on md+ */}
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center">
              <div className="pointer-events-auto hidden pl-4 md:flex">
                <CarouselPrevious variant="outline" size="icon" />
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
              <div className="pointer-events-auto hidden pr-4 md:flex">
                <CarouselNext variant="outline" size="icon" />
              </div>
            </div>
          </Carousel>
        </div>
      </div>
    </div>
  );
};

const faqData = [
  {
    id: 1,
    question: 'What sets your diamond collection apart from others?',
    answer:
      "Our diamond collection represents the pinnacle of excellence in the industry. Each stone is meticulously selected based on superior cut quality that exceeds standard specifications. Our exclusive relationships with the world's most prestigious diamond cutters ensure access to extraordinary stones rarely found elsewhere.",
  },
  {
    id: 2,
    question: 'How are your diamonds certified and graded?',
    answer:
      'Every diamond in our collection undergoes rigorous evaluation and receives certification from the most respected gemological institutes, including GIA and AGS. Additionally, our in-house gemologists conduct a proprietary 40-point assessment that evaluates aspects of brilliance and fire that exceed standard certification parameters.',
  },
  {
    id: 3,
    question: 'Do you offer both natural and lab-grown diamonds?',
    answer:
      'Yes, we provide both exceptional natural diamonds and premium lab-grown diamonds to meet diverse preferences and budgets. Our natural diamonds are carefully sourced for their rarity and geological significance, while our lab-grown diamonds offer identical physical and chemical properties with innovative precision. Each category maintains our exacting standards for cut quality and brilliance.',
  },
  {
    id: 4,
    question: 'What diamond shapes and sizes are available in your collection?',
    answer:
      'Our extensive inventory features diamonds across a comprehensive range of carat weights and all popular shapes. We specialize in round, oval, cushion, emerald, princess, radiant, asscher, pear, marquise, and heart-shaped diamonds. Whether you seek a delicate accent stone or a spectacular statement piece, our collection accommodates every vision and preference.',
  },
  {
    id: 5,
    question: 'How do you ensure ethical sourcing of your diamonds?',
    answer:
      "We maintain the highest ethical standards through our comprehensive sourcing protocols. Every natural diamond is certified conflict-free and traceable to its origin mine. We partner exclusively with suppliers who adhere to the Kimberley Process and support local mining communities through fair trade practices. Our supply chain transparency ensures each diamond's journey upholds our commitment to responsible luxury.",
  },
  {
    id: 6,
    question: 'How is your diamond pricing structured?',
    answer:
      'Our transparent pricing methodology provides detailed breakdowns for each diamond, allowing you to understand exactly how costs are derived. Factors include the four Cs (cut, color, clarity, carat), certification level, rarity, and market positioning. This comprehensive approach ensures you receive exceptional value while understanding the investment in your chosen piece.',
  },
  {
    id: 7,
    question: 'Can I purchase just the diamond or do I need to buy a complete jewelry piece?',
    answer:
      'We offer complete flexibility in your purchase options. You may acquire loose diamonds for your own custom projects or choose from our curated selection of exquisite settings. Our design consultants can guide you through pairing your chosen diamond with the perfect mounting, or we can deliver your stone independently with full certification and documentation.',
  },
  {
    id: 8,
    question: 'What assurances do you offer with each purchase?',
    answer:
      'Our Diamond Assurance Program provides lifetime maintenance, complimentary refinishing services, and a comprehensive insurance valuation. Additionally, we offer a 60-day satisfaction guarantee, allowing you to ensure your selection perfectly aligns with your expectations. Each piece comes with documentation of its journey from rough stone to finished creation.',
  },
  {
    id: 9,
    question: 'How are your pieces delivered?',
    answer:
      "Each creation is delivered via our secure white-glove courier service, available worldwide. For local clients, we offer private in-home presentation. Every piece arrives in our signature presentation case, which includes integrated lighting elements to showcase your diamond's exceptional brilliance, along with its complete documentation and provenance.",
  },
];

export const IndexFAQSection = ({ className }: React.ComponentProps<'div'>) => {
  return (
    <PageSectionLarge className={`py-16 sm:py-20 lg:py-24 ${className || ''}`}>
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12 text-center sm:mb-16">
          <TypographyTitle className="mb-4 tracking-wide text-gray-900 sm:mb-6">
            Client Experience
          </TypographyTitle>
          <div className="mx-auto max-w-2xl">
            <TypographyText className="text-muted-foreground text-lg leading-relaxed sm:text-xl">
              Discover our approach to exceptional service and craftsmanship
            </TypographyText>
          </div>
        </div>

        <Accordion type="multiple" className="space-y-4">
          {faqData.map((item) => (
            <AccordionItem key={item.id} value={String(item.id)}>
              <AccordionTrigger className="hover:no-underline">
                <TypographyLabel className="text-lg tracking-wide">{item.question}</TypographyLabel>
              </AccordionTrigger>
              <AccordionContent>
                <TypographyText className="leading-relaxed tracking-wide">
                  {item.answer}
                </TypographyText>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </PageSectionLarge>
  );
};
